import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { LEAVE_STATUS, TRAINING_TYPES, fmtDateTH, toBE } from './hrShared'

const emptyForm = {
  tr_type: 'training', tr_title: '', tr_organizer: '', tr_location: '',
  tr_start_date: '', tr_end_date: '', tr_detail: '',
}

// การ์ดรายการแจ้งไปอบรม/ประชุม 1 รายการ สำหรับมุมมองมือถือ
const MyTrainingCard = ({ r, onCancel }) => {
  const ls = LEAVE_STATUS[r.tr_status] || LEAVE_STATUS.pending
  const tt = TRAINING_TYPES[r.tr_type] || TRAINING_TYPES.training
  return (
    <div className="border rounded p-3">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <Badge bg={tt.bg} className="me-1">{tt.label}</Badge>
          <span className="fw-semibold">{r.tr_title}</span>
        </div>
        <Badge bg={ls.bg}>{ls.label}</Badge>
      </div>
      <div className="mt-2 pt-2 border-top fs-sm">
        <div><span className="text-muted">วันที่:</span> {fmtDateTH(r.tr_start_date)} - {fmtDateTH(r.tr_end_date)} ({r.tr_days} วัน)</div>
        {r.tr_organizer && <div><span className="text-muted">หน่วยงานผู้จัด:</span> {r.tr_organizer}</div>}
        {r.tr_location && <div><span className="text-muted">สถานที่:</span> {r.tr_location}</div>}
        {r.tr_detail && <div><span className="text-muted">รายละเอียด:</span> {r.tr_detail}</div>}
        {r.approved_by_name && <div className="text-muted fs-xs mt-1">{r.tr_status === 'approved' ? 'ผู้อนุมัติ' : 'ผู้พิจารณา'}: {r.approved_by_name}</div>}
        {r.approve_note && <div className="text-muted fs-xs mt-1">{r.approve_note}</div>}
      </div>
      {r.tr_status === 'pending' && (
        <div className="mt-2 pt-2 border-top d-flex gap-2">
          <Button size="sm" variant="outline-danger" className="flex-grow-1" onClick={onCancel}>ยกเลิก</Button>
        </div>
      )}
    </div>
  )
}

export default function MyTraining() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(String(currentYear))
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get(`/api/hr/me/training?year=${year}`)
      if (res.data.success) setRequests(res.data.data.requests)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [year])

  useEffect(() => { fetchData() }, [fetchData])

  const openModal = () => {
    const today = new Date().toLocaleDateString('en-CA')
    setForm({ ...emptyForm, tr_start_date: today, tr_end_date: today })
    setModalError('')
    setShowModal(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (form.tr_end_date < form.tr_start_date) {
      setModalError('วันสิ้นสุดต้องไม่อยู่ก่อนวันเริ่มต้น')
      return
    }
    setSaving(true)
    setModalError('')
    try {
      const res = await axiosInstance.post('/api/hr/me/training', form)
      if (res.data.success) {
        setShowModal(false)
        Swal.fire({ icon: 'success', title: 'แจ้งเรียบร้อยแล้ว', text: 'รอการอนุมัติจากงานบุคลากร', timer: 2000, showConfirmButton: false })
        fetchData()
      }
    } catch (err) {
      setModalError(err.response?.data?.message || 'แจ้งไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = async (r) => {
    const tt = TRAINING_TYPES[r.tr_type] || TRAINING_TYPES.training
    const result = await Swal.fire({
      icon: 'warning',
      title: `ยกเลิกการแจ้งไป${tt.label}?`,
      text: `${r.tr_title} · ${fmtDateTH(r.tr_start_date)} - ${fmtDateTH(r.tr_end_date)}`,
      showCancelButton: true,
      confirmButtonText: 'ยกเลิกรายการ',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#dc3545',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.put(`/api/hr/me/training/${r.tr_id}/cancel`)
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ยกเลิกไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const yearOptions = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2]

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-presentation-line me-2 text-primary" />
              แจ้งไปอบรม / ประชุม / กิจกรรม
            </h4>
            <div className="d-flex gap-2">
              <Form.Select size="sm" style={{ width: 130 }} value={year} onChange={e => setYear(e.target.value)}>
                {yearOptions.map(y => <option key={y} value={y}>พ.ศ. {toBE(y)}</option>)}
              </Form.Select>
              <Button variant="primary" size="sm" onClick={openModal}>
                <i className="ri-add-line me-1" />แจ้งรายการ
              </Button>
            </div>
          </div>

          <Alert variant="light" className="border fs-sm py-2">
            <i className="ri-information-line me-1" />
            สำหรับแจ้งงานบุคลากรเมื่อต้องไปอบรม ประชุม หรือทำกิจกรรมนอกโรงเรียน (ไม่ตัดโควตาวันลา) —
            เมื่ออนุมัติแล้ว หัวหน้าวิชาการจะเห็นรายการเพื่อวางแผนการสอนแทน
          </Alert>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                <Card.Title as="h6" className="mb-0">ประวัติการแจ้งไปอบรม/ประชุม/กิจกรรม ปี พ.ศ. {toBE(year)}</Card.Title>
                <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                  <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                    onClick={() => setView('table')} title="มุมมองตาราง">
                    <i className="ri-table-line" />
                  </button>
                  <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                    onClick={() => setView('card')} title="มุมมองการ์ด">
                    <i className="ri-layout-grid-line" />
                  </button>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {requests.length === 0 ? (
                  <div className="text-center text-muted py-4">ยังไม่มีรายการในปีนี้</div>
                ) : view === 'card' ? (
                  <div className="p-3">
                    <Row className="g-3">
                      {requests.map(r => (
                        <Col key={r.tr_id} xs={12} md={6} xl={4}>
                          <MyTrainingCard r={r} onCancel={() => handleCancel(r)} />
                        </Col>
                      ))}
                    </Row>
                  </div>
                ) : (
                <Table responsive hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th>ประเภท</th>
                      <th>หัวข้อ</th>
                      <th>วันที่</th>
                      <th className="text-end">จำนวนวัน</th>
                      <th>หน่วยงานผู้จัด</th>
                      <th>สถานที่</th>
                      <th>สถานะ</th>
                      <th style={{ width: 100 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map(r => {
                      const ls = LEAVE_STATUS[r.tr_status] || LEAVE_STATUS.pending
                      const tt = TRAINING_TYPES[r.tr_type] || TRAINING_TYPES.training
                      return (
                        <tr key={r.tr_id}>
                          <td><Badge bg={tt.bg}>{tt.label}</Badge></td>
                          <td className="text-truncate" style={{ maxWidth: 220 }} title={r.tr_title}>{r.tr_title}</td>
                          <td>{fmtDateTH(r.tr_start_date)} - {fmtDateTH(r.tr_end_date)}</td>
                          <td className="text-end">{r.tr_days}</td>
                          <td className="text-truncate" style={{ maxWidth: 150 }} title={r.tr_organizer || ''}>{r.tr_organizer || '-'}</td>
                          <td className="text-truncate" style={{ maxWidth: 150 }} title={r.tr_location || ''}>{r.tr_location || '-'}</td>
                          <td>
                            <Badge bg={ls.bg}>{ls.label}</Badge>
                            {r.approved_by_name && (
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>{r.tr_status === 'approved' ? 'ผู้อนุมัติ' : 'ผู้พิจารณา'}: {r.approved_by_name}</div>
                            )}
                            {r.approve_note && (
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>{r.approve_note}</div>
                            )}
                          </td>
                          <td className="text-end">
                            {r.tr_status === 'pending' && (
                              <Button size="sm" variant="outline-danger" onClick={() => handleCancel(r)}>
                                ยกเลิก
                              </Button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
                )}
              </Card.Body>
            </Card>
          )}
        </Container>
      </div>
      <Footer />

      {/* Submit-request modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">แจ้งไปอบรม / ประชุม / กิจกรรม</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}

            <Row className="g-2 mb-2">
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">ประเภท</Form.Label>
                <Form.Select size="sm" required value={form.tr_type}
                  onChange={e => setForm(f => ({ ...f, tr_type: e.target.value }))}>
                  {Object.entries(TRAINING_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </Form.Select>
              </Col>
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">วันเริ่มต้น</Form.Label>
                <Form.Control size="sm" type="date" required value={form.tr_start_date}
                  onChange={e => setForm(f => ({ ...f, tr_start_date: e.target.value, tr_end_date: f.tr_end_date < e.target.value ? e.target.value : f.tr_end_date }))} />
              </Col>
              <Col xs={4}>
                <Form.Label className="fs-sm mb-1">วันสิ้นสุด</Form.Label>
                <Form.Control size="sm" type="date" required min={form.tr_start_date} value={form.tr_end_date}
                  onChange={e => setForm(f => ({ ...f, tr_end_date: e.target.value }))} />
              </Col>
            </Row>

            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">หัวข้อการอบรม/ประชุม/กิจกรรม <span className="text-danger">*</span></Form.Label>
              <Form.Control size="sm" type="text" maxLength={255} required value={form.tr_title}
                onChange={e => setForm(f => ({ ...f, tr_title: e.target.value }))} />
            </Form.Group>

            <Row className="g-2 mb-2">
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">หน่วยงานผู้จัด <span className="text-muted">— ไม่บังคับ</span></Form.Label>
                <Form.Control size="sm" type="text" maxLength={255} value={form.tr_organizer}
                  onChange={e => setForm(f => ({ ...f, tr_organizer: e.target.value }))} />
              </Col>
              <Col xs={6}>
                <Form.Label className="fs-sm mb-1">สถานที่ <span className="text-muted">— ไม่บังคับ</span></Form.Label>
                <Form.Control size="sm" type="text" maxLength={255} value={form.tr_location}
                  onChange={e => setForm(f => ({ ...f, tr_location: e.target.value }))} />
              </Col>
            </Row>

            <Form.Group>
              <Form.Label className="fs-sm mb-1">รายละเอียดเพิ่มเติม <span className="text-muted">— ไม่บังคับ</span></Form.Label>
              <Form.Control size="sm" as="textarea" rows={2} value={form.tr_detail}
                onChange={e => setForm(f => ({ ...f, tr_detail: e.target.value }))} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-send-plane-line me-1" />}
              แจ้งรายการ
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
